<template>
  <div>
    <!-- Cabecera -->
    <v-card outlined rounded="lg" class="mb-3 pa-4">
      <div class="orbitron-title mb-2">Mis Ligas</div>

      <div class="text-body-2 grey--text mb-4">
        Participa en diferentes mercados regionales y compite por el primer puesto
      </div>

      <v-row dense>
        <v-col cols="12" sm="6">
          <v-btn
            block
            class="crear-liga-btn"
            :disabled="isAreaOperationRunning"
            @click="showCreateDialog = true"
          >
            <span class="material-symbols-outlined area-btn-icon">
              add_circle
            </span>
            Crear liga
          </v-btn>
        </v-col>

        <v-col cols="12" sm="6">
          <v-btn
            block
            class="unirse-liga-btn"
            :disabled="isAreaOperationRunning"
            @click="showJoinDialog = true"
          >
            <span class="material-symbols-outlined area-btn-icon">
              group_add
            </span>
            Unirse con código
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <!-- Sin ligas -->
    <v-alert
      v-if="!areas.length"
      dense
      outlined
      class="mb-3 alert-ligas-vacio"
    >
      Todavía no perteneces a ninguna liga. Crea una nueva o únete con un código.
    </v-alert>

    <!-- Una tarjeta por liga -->
    <v-card
      v-for="area in areas"
      :key="area.id"
      class="mb-3 pa-4 area-card"
      outlined
      rounded="lg"
    >
      <v-card-text>
        <!-- Cabecera de esta liga -->
        <div class="area-header">
          <div class="area-heading">
            <div class="d-flex align-center flex-wrap">
              <div class="text-subtitle-1 font-weight-medium">
                {{ area.name }}
              </div>

              <v-chip
                v-if="isAreaActive(area)"
                class="ml-2"
                small
                color="green"
                text-color="white"
              >
                Seleccionada
              </v-chip>
              <v-chip
                v-if="area.status === 'waiting'"
                class="ml-2"
                small
                color="orange"
                text-color="white"
              >
                Esperando inicio
              </v-chip>

              <v-chip
                v-if="
                  area.status === 'active' &&
                  !isAreaTimeExpired(area)
                "
                class="ml-2"
                small
                color="green"
                text-color="white"
              >
                En curso
              </v-chip>

              <v-chip
                v-if="
                  area.status === 'finished' ||
                  isAreaTimeExpired(area)
                "
                class="ml-2"
                small
                color="grey"
                text-color="white"
              >
                Finalizada
              </v-chip>

              <v-chip
                v-if="area.isFull"
                class="ml-2"
                small
                color="red"
                text-color="white"
              >
                Completa
              </v-chip>
            </div>

            <div class="text-caption grey--text">
              {{ area.region }}
            </div>

            <div v-if="area.code" class="text-caption mt-1">
              Código:
              <strong>{{ area.code }}</strong>

              <v-btn
                x-small
                text
                color="primary"
                class="ml-1"
                @click.stop="copyCode(area.code)"
              >
                Copiar
              </v-btn>
            </div>
          </div>

          <!-- Elimina la liga  -->
          <v-tooltip
            v-if="canDeleteArea(area)"
            bottom
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                class="delete-icon-btn"
                :loading="deletingAreaId === String(area.id)"
                :disabled="isAreaOperationRunning"
                :aria-label="`Eliminar la liga ${area.name}`"
                v-bind="attrs"
                v-on="on"
                @click.stop="confirmDeleteArea(area)"
              >
                <span class="material-symbols-outlined delete-icon">
                  delete
                </span>
              </v-btn>
            </template>

            <span>Eliminar esta liga</span>
          </v-tooltip>
        </div>

        <v-divider class="my-3" />

        <!-- Estadísticas -->
        <v-row dense>
          <v-col cols="4">
            <div class="text-caption grey--text">Mi posición</div>
            <div class="text-subtitle-2 font-weight-medium">
            {{ formatAreaRank(area.myRank) }}
            </div>
          </v-col>

          <v-col cols="4">
            <div class="text-caption grey--text">Mis puntos</div>
            <div class="text-subtitle-2 font-weight-medium">
              {{ Number(area.myPoints || 0).toLocaleString("es-ES") }}
            </div>
          </v-col>

          <v-col cols="4">
            <div class="text-caption grey--text">
              Tiempo restante
            </div>

            <div
              class="text-subtitle-2 font-weight-medium league-countdown"
              :class="{
                'league-countdown-finished':
                  isAreaTimeExpired(area)
              }"
            >
              {{ formatAreaCountdown(area) }}
            </div>
          </v-col>
        </v-row>

        <div class="text-caption grey--text mt-3">
          👥 {{ area.participants || 0 }} /
          {{ area.maxParticipants || 15 }} participantes
        </div>
        <div class="text-caption grey--text mt-1">
          Mínimo para comenzar:
        <strong>{{ area.minParticipants || 3 }}</strong>

        <template v-if="area.status === 'active'">
          · Duración:
          <strong>{{ area.durationDays }} días</strong>
        </template>

        <template v-else-if="area.status === 'waiting'">
          · Duración:
          <strong>se elige al iniciar</strong>
        </template>
        </div>
            <div
              v-if="area.status === 'waiting'"
              class="mt-3"
            >
              <v-alert
                v-if="
                  Number(area.participants || 0) <
                  Number(area.minParticipants || 3)
                "
                dense
                outlined
                type="info"
                class="mb-2"
              >
                Esperando jugadores:
                {{ area.participants || 0 }} /
                {{ area.minParticipants || 3 }} mínimos
              </v-alert>

              <div
                v-if="String(area.role || '').toLowerCase() === 'admin'"
                class="admin-league-actions"
              >
                <v-btn
                  class="entrar-liga-btn admin-league-action-btn"
                  :loading="startingAreaId === String(area.id)"
                  :disabled="
                    isAreaOperationRunning ||
                    !area.canStart
                  "
                  @click="confirmStartArea(area)"
                >
                  <span class="material-symbols-outlined area-btn-icon">
                    play_circle
                  </span>

                  Iniciar liga
                </v-btn>

                <v-btn
                  class="manage-players-btn admin-league-action-btn"
                  outlined
                  :disabled="isAreaOperationRunning"
                  @click.stop="openPlayersManager(area)"
                >
                  <v-icon left>
                    mdi-account-multiple
                  </v-icon>

                  Gestionar jugadores
                </v-btn>
              </div>
            </div>
        <!-- Acciones de esta liga -->
        <div
          class="area-actions mt-3"
          
        >
          <v-btn
            v-if="!isAreaActive(area)"
            block
            class="entrar-liga-btn"
            :loading="enteringAreaId === String(area.id)"
            :disabled="
              isAreaOperationRunning &&
              enteringAreaId !== String(area.id)
            "
            @click="enterArea(area.id)"
          >
            <span class="material-symbols-outlined area-btn-icon">
              login
            </span>

            Seleccionar y entrar
          </v-btn>

          <v-btn
            v-if="isAreaActive(area)"
            block
            outlined
            class="salir-area"
            :loading="leavingAreaId === String(area.id)"
            :disabled="isAreaOperationRunning"
            @click="confirmLeaveArea(area)"
          >
            <span class="material-symbols-outlined mr-1">
              logout
            </span>
            Salir de la liga
          </v-btn>
        </div>
      </v-card-text>
    </v-card>

    <!-- Crear liga -->
    <v-dialog v-model="showCreateDialog" max-width="420" persistent>
      <v-card>
        <v-card-title>Crear nueva liga</v-card-title>

        <v-card-text>
          <v-text-field
            v-model="newLeagueName"
            label="Nombre de la liga"
            outlined
            dense
            counter="100"
            maxlength="100"
            :disabled="loadingCreate"
            @keyup.enter="createLeague"
          />
          <v-alert
            dense
            outlined
            type="info"
            class="mt-3 mb-3"
          >
            <strong>Mínimo de jugadores: 3</strong>
            <br>
            Máximo de jugadores: 15
          </v-alert>

          <v-text-field
            v-model.number="newLeagueDurationDays"
            label="Duración de la liga"
            type="number"
            min="1"
            suffix="días"
            outlined
            dense
            hint="Recomendado: 60 días (aprox. 2 meses)"
            persistent-hint
            :disabled="loadingCreate"
          />

          <v-alert
            v-if="createError"
            type="error"
            dense
            outlined
            class="mt-3"
          >
            {{ createError }}
          </v-alert>
        </v-card-text>

        <v-card-actions>
          <v-spacer />

          <v-btn
            text
            :disabled="loadingCreate"
            @click="closeCreateDialog"
          >
            Cancelar
          </v-btn>

          <v-btn
            color="primary"
            :loading="loadingCreate"
            :disabled="
              loadingCreate ||
              !String(newLeagueName || '').trim()
            "
            @click="createLeague"
          >
            Crear
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Unirse con código -->
    <v-dialog v-model="showJoinDialog" max-width="420" persistent>
      <v-card>
        <v-card-title>Unirse a una liga</v-card-title>

        <v-card-text>
          <v-text-field
            v-model="joinCode"
            label="Código de la liga"
            outlined
            dense
            maxlength="30"
            :disabled="loadingJoin"
            @input="normalizeJoinCode"
            @keyup.enter="joinLeagueByCode"
          />

          <v-alert dense outlined type="info" class="mt-2">
            Cada liga tiene un límite máximo de 15 jugadores.
          </v-alert>

          <v-alert
            v-if="joinError"
            type="error"
            dense
            outlined
            class="mt-3"
          >
            {{ joinError }}
          </v-alert>
        </v-card-text>

        <v-card-actions>
          <v-spacer />

          <v-btn
            text
            :disabled="loadingJoin"
            @click="closeJoinDialog"
          >
            Cancelar
          </v-btn>

          <v-btn
            color="primary"
            :loading="loadingJoin"
            :disabled="
              loadingJoin ||
              !String(joinCode || '').trim()
            "
            @click="joinLeagueByCode"
          >
            Unirme
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
  v-model="playersDialog"
  max-width="650"
>
  <v-card class="players-manager-card">
    <v-card-title class="players-manager-title">
      <div>
        <div class="orbitron-title">
          Gestionar jugadores
        </div>

        <div
          v-if="selectedAreaForPlayers"
          class="text-body-2 grey--text mt-1"
        >
          {{ selectedAreaForPlayers.name }}
        </div>
      </div>

      <v-spacer />

      <v-btn
  icon
  class="players-close-btn"
  aria-label="Cerrar"
  @click="closePlayersManager"
>
  <span class="material-symbols-outlined players-close-icon">
    close
  </span>
</v-btn>
    </v-card-title>

    <v-divider />

    <v-card-text class="pa-4">
      <div
        v-if="playersLoading"
        class="text-center py-8"
      >
        <v-progress-circular
          indeterminate
          color="primary"
        />

        <div class="mt-3">
          Cargando jugadores...
        </div>
      </div>

      <div
        v-else-if="!areaParticipants.length"
        class="text-center grey--text py-8"
      >
        No hay jugadores en esta liga.
      </div>

      <div v-else>
        <div
          v-for="participant in areaParticipants"
          :key="participant.userId"
          class="player-manager-row"
        >
          <div class="player-manager-info">
            <v-avatar
              size="38"
              class="mr-3"
            >
              <v-icon>
                mdi-account-circle
              </v-icon>
            </v-avatar>

            <div>
              <div class="font-weight-bold">
                {{ participant.username || `Usuario ${participant.userId}` }}
              </div>

              <div
                v-if="participant.role"
                class="text-caption grey--text"
              >
                {{
                  String(participant.role).toLowerCase() === "admin"
                    ? "Administrador"
                    : "Jugador"
                }}
              </div>
            </div>
          </div>

          <v-btn
            v-if="canRemoveParticipant(participant)"
            class="expel-player-btn"
            small
            outlined
            :loading="
              String(removingPlayerId) ===
              String(participant.userId)
            "
            :disabled="removingPlayerId !== null"
            @click="confirmRemoveParticipant(participant)"
          >

            Expulsar
          </v-btn>

          <v-chip
            v-else-if="participant.isMe"
            small
            outlined
          >
            Tú
          </v-chip>

          <v-chip
            v-else-if="
              String(participant.role || '').toLowerCase() === 'admin'
            "
            small
            outlined
          >
            Admin
          </v-chip>
        </div>
      </div>
    </v-card-text>
  </v-card>
</v-dialog>
  </div>
</template>

<script>
import Swal from "sweetalert2";
import { useGameStore } from "@/stores/game";

export default {
  name: "MyAreas",

  props: {
    areas: {
      type: Array,
      required: true,
    },

    currentAreaId: {
      type: [String, Number],
      default: "",
    },
  },

  data() {
    return {
      showCreateDialog: false,
      showJoinDialog: false,

      newLeagueName: "",
      newLeagueDurationDays: 60,
      joinCode: "",

      loadingCreate: false,
      loadingJoin: false,

      createError: "",
      joinError: "",

      enteringAreaId: null,
      leavingAreaId: null,
      deletingAreaId: null,
      startingAreaId: null,
      now: Date.now(),
      countdownTimer: null,
      playersDialog: false,
      playersLoading: false,
      removingPlayerId: null,
      selectedAreaForPlayers: null,
      areaParticipants: [],
      store: useGameStore(),
    };
  },

  computed: {
    isAreaOperationRunning() {
      return (
        this.loadingCreate ||
        this.loadingJoin ||
        this.enteringAreaId !== null ||
        this.leavingAreaId !== null ||
        this.deletingAreaId !== null ||
        this.startingAreaId !== null
      );
    },
  },
  mounted() {
  this.now = Date.now();

  this.countdownTimer = window.setInterval(
    () => {
      this.now = Date.now();
    },
    1000
  );
},

beforeDestroy() {
  if (this.countdownTimer) {
    window.clearInterval(
      this.countdownTimer
    );

    this.countdownTimer = null;
  }
},

  methods: {
    canManagePlayers(area) {
  if (!area) return false;

  return String(area.role || "").toLowerCase() === "admin";
},

async openPlayersManager(area) {
  if (
    !area ||
    !this.canManagePlayers(area) ||
    this.isAreaOperationRunning
  ) {
    return;
  }

  this.selectedAreaForPlayers = area;
  this.areaParticipants = [];
  this.playersDialog = true;
  this.playersLoading = true;

  try {
    this.areaParticipants =
      await this.store.fetchAreaParticipants(area.id);
  } catch (error) {
    this.playersDialog = false;

    await Swal.fire({
      icon: "error",
      title: "No se pudieron cargar los jugadores",
      text:
        error?.message ||
        "No se pudieron cargar los participantes de la liga.",
      confirmButtonText: "Entendido",
      buttonsStyling: false,
      customClass: {
        confirmButton: "liga-eliminada-confirmar",
      },
    });
  } finally {
    this.playersLoading = false;
  }
},

closePlayersManager() {
  if (this.removingPlayerId !== null) {
    return;
  }

  this.playersDialog = false;
  this.selectedAreaForPlayers = null;
  this.areaParticipants = [];
},

canRemoveParticipant(participant) {
  if (!participant) {
    return false;
  }

  if (participant.isMe) {
    return false;
  }

  if (
    String(participant.role || "").toLowerCase() === "admin"
  ) {
    return false;
  }

  return Number(participant.userId) > 0;
},

async confirmRemoveParticipant(participant) {
  if (
    !participant ||
    !this.selectedAreaForPlayers ||
    this.removingPlayerId !== null
  ) {
    return;
  }

  const userId = participant.userId;

  if (!this.canRemoveParticipant(participant)) {
    return;
  }

  const username =
  participant.username ||
  `Usuario ${userId}`;

  const result = await Swal.fire({
    icon: "warning",
    title: "¿Expulsar jugador?",

    html: `
      <div style="text-align:left">
        <p>
          Vas a expulsar a
          <b>${this.escapeHtml(username)}</b>
          de la liga
          <b>${this.escapeHtml(this.selectedAreaForPlayers.name)}</b>.
        </p>

        <p>
          Se eliminarán todos sus datos asociados
          exclusivamente a esta liga.
        </p>

        <p>
          Sus datos en otras ligas y su cuenta
          de usuario no se eliminarán.
        </p>

        <p>
          <b>Esta acción no se puede deshacer.</b>
        </p>
      </div>
    `,

    showCancelButton: true,
    confirmButtonText: "Expulsar jugador",
    cancelButtonText: "Cancelar",

    allowOutsideClick: false,
    allowEscapeKey: false,

    buttonsStyling: false,

    customClass: {
      popup: "expulsar-jugador-popup",
      actions: "expulsar-jugador-acciones",
      confirmButton: "expulsar-jugador-confirmar",
      cancelButton: "expulsar-jugador-cancelar",
    },
  });

  if (!result.isConfirmed) {
    return;
  }

  this.removingPlayerId = userId;

  try {
    await this.store.removeAreaParticipant(
      this.selectedAreaForPlayers.id,
      userId
    );

    this.areaParticipants =
      this.areaParticipants.filter(
        player =>
          String(player.userId) !== String(userId)
      );

    await Swal.fire({
      icon: "success",
      title: "Jugador expulsado",
      text: `${username} ha sido expulsado de la liga.`,
      confirmButtonText: "Entendido",
      buttonsStyling: false,
      customClass: {
        confirmButton: "liga-eliminada-confirmar",
      },
    });
  } catch (error) {
    await Swal.fire({
      icon: "error",
      title: "No se pudo expulsar",
      text:
        error?.message ||
        "No se pudo expulsar al jugador.",
      confirmButtonText: "Entendido",
      buttonsStyling: false,
      customClass: {
        confirmButton: "liga-eliminada-confirmar",
      },
    });
  } finally {
    this.removingPlayerId = null;
  }
},
  getAreaRemainingMilliseconds(area) {
  if (
    !area ||
    area.status !== "active" ||
    !area.endAt
  ) {
    return 0;
  }

  const endTimestamp =
    new Date(area.endAt).getTime();

  if (!Number.isFinite(endTimestamp)) {
    return 0;
  }

  return Math.max(
    0,
    endTimestamp - this.now
  );
},

isAreaTimeExpired(area) {
  if (!area) {
    return false;
  }

  if (area.status === "finished") {
    return true;
  }

  if (
    area.status !== "active" ||
    !area.endAt
  ) {
    return false;
  }

  const endTimestamp =
    new Date(area.endAt).getTime();

  if (!Number.isFinite(endTimestamp)) {
    return false;
  }

  return this.now >= endTimestamp;
},

formatAreaCountdown(area) {
  if (!area) {
    return "—";
  }

  if (area.status === "waiting") {
    return "Sin iniciar";
  }

  if (
    area.status === "finished" ||
    this.isAreaTimeExpired(area)
  ) {
    return "Finalizada";
  }

  if (
    area.status !== "active" ||
    !area.endAt
  ) {
    return "—";
  }

  const remaining =
    this.getAreaRemainingMilliseconds(
      area
    );

  const totalSeconds = Math.floor(
    remaining / 1000
  );

  const days = Math.floor(
    totalSeconds / 86400
  );

  const hours = Math.floor(
    (totalSeconds % 86400) / 3600
  );

  const minutes = Math.floor(
    (totalSeconds % 3600) / 60
  );

  const seconds =
    totalSeconds % 60;

  const pad = (value) =>
    String(value).padStart(2, "0");

  return (
    `${days}d ` +
    `${pad(hours)}:` +
    `${pad(minutes)}:` +
    `${pad(seconds)}`
  );
},
    async confirmStartArea(area) {
  if (!area || !area.canStart) {
    return;
  }

  const safeName = this.escapeHtml(area.name);

  const participants = Number(
    area.participants || 0
  );

  const result = await Swal.fire({
    icon: "question",
    title: "¿Iniciar la liga?",

    html: `
      <div class="iniciar-liga-content">
        <p>
          Vas a iniciar
          <strong>${safeName}</strong>.
        </p>

        <p>
          Jugadores inscritos:
          <strong>${participants}</strong>.
        </p>

        <label class="iniciar-liga-label">
          Duración de esta temporada
        </label>

        <div class="iniciar-liga-duration-wrapper">
          <input
            id="swal-duration-days"
            type="number"
            min="1"
            step="1"
            value="60"
            class="iniciar-liga-duration-input"
          >

          <span class="iniciar-liga-duration-suffix">
            días
          </span>
        </div>

        <div class="iniciar-liga-hint">
          Recomendado: 60 días (aprox. 2 meses)
        </div>

        <p class="iniciar-liga-warning">
          Una vez iniciada ya no podrán incorporarse
          nuevos jugadores.
        </p>
      </div>
    `,

    showCancelButton: true,
    confirmButtonText: "Iniciar liga",
    cancelButtonText: "Cancelar",

    buttonsStyling: false,

    customClass: {
      popup: "iniciar-liga-popup",
      actions: "iniciar-liga-acciones",
      confirmButton: "iniciar-liga-confirmar",
      cancelButton: "iniciar-liga-cancelar",
    },

    preConfirm: () => {
      const input = document.getElementById(
        "swal-duration-days"
      );

      const durationDays = Number(
        input?.value
      );

      if (
        !Number.isInteger(durationDays) ||
        durationDays < 1
      ) {
        Swal.showValidationMessage(
          "La duración debe ser de al menos 1 día."
        );

        return false;
      }

      return durationDays;
    },
  });

  if (!result.isConfirmed) {
    return;
  }

  this.startArea(
    area.id,
    result.value
  );
},

startArea(areaId, durationDays) {
  const normalizedAreaId = String(
    areaId || ""
  );

  const normalizedDurationDays = Number(
    durationDays
  );

  if (
    !normalizedAreaId ||
    !Number.isInteger(normalizedDurationDays) ||
    normalizedDurationDays < 1 ||
    this.isAreaOperationRunning
  ) {
    return;
  }

  this.startingAreaId = normalizedAreaId;

  this.$emit("start-area", {
    areaId: normalizedAreaId,
    durationDays: normalizedDurationDays,

    done: async () => {
      this.startingAreaId = null;

      await Swal.fire({
        icon: "success",
        title: "Liga iniciada",
        text:
          "La competición ha comenzado. " +
          "La cuenta atrás ya está en marcha.",
        confirmButtonText: "Entendido",

        buttonsStyling: false,

        customClass: {
          confirmButton:
            "liga-eliminada-confirmar",
        },
      });
    },

    fail: async (message) => {
      this.startingAreaId = null;

      await Swal.fire({
        icon: "error",
        title: "No se pudo iniciar",
        text:
          message ||
          "No se pudo iniciar la liga.",
        confirmButtonText: "Entendido",

        buttonsStyling: false,

        customClass: {
          confirmButton:
            "liga-eliminada-confirmar",
        },
      });
    },
  });
},
    formatAreaRank(value) {
  const rank = Number(value);

  if (!Number.isInteger(rank) || rank <= 0) {
    return "Sin puntos";
  }

  return `#${rank}`;
},
    isAreaActive(area) {
      if (!area) return false;

      return (
        area.isActive === true ||
        String(area.id) === String(this.currentAreaId)
      );
    },

    canDeleteArea(area) {
      if (!area) return false;

      // La liga oficial queda protegida.
      if (String(area.id) === "1") return false;

      return (
        area.canDelete === true ||
        String(area.role || "").toLowerCase() === "admin"
      );
    },

    escapeHtml(value) {
      return String(value == null ? "" : value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
    },

    async confirmDeleteArea(area) {
      if (
        !area ||
        !this.canDeleteArea(area) ||
        this.isAreaOperationRunning
      ) {
        return;
      }

      const confirmationText = String(area.code || area.name || "").trim();
      
      

      const result = await Swal.fire({
  icon: "warning",
  title: "¿Eliminar la liga?",

  html: `
    <div style="text-align:left">
      <p>
        Vas a eliminar permanentemente
        <b>${area.name}</b>.
      </p>

      <p>
        Se borrarán los participantes, activos,
        puntos, transacciones, pujas y premios
        asociados a esta liga.
      </p>

      <p>
        Esta acción no se puede deshacer.
      </p>

      <p>
        Escribe <b>${confirmationText}</b>
        para confirmar.
      </p>
    </div>
  `,

  input: "text",
  inputPlaceholder: confirmationText,

  showCancelButton: true,
  confirmButtonText: "Eliminar liga",
  cancelButtonText: "Cancelar",

  allowOutsideClick: false,
  allowEscapeKey: false,

  buttonsStyling: false,

  customClass: {
    popup: "eliminar-liga-popup",
    input: "eliminar-liga-input",
    actions: "eliminar-liga-acciones",
    confirmButton: "eliminar-liga-confirmar",
    cancelButton: "eliminar-liga-cancelar",
  },

  preConfirm: (value) => {
    const entered = String(value || "").trim();

    if (entered !== confirmationText) {
      Swal.showValidationMessage(
        "El código o nombre introducido no coincide."
      );

      return false;
    }

    return entered;
  },
});

      if (result.isConfirmed) {
        this.deleteArea(area.id);
      }
    },

    deleteArea(areaId) {
      const normalizedAreaId = String(areaId || "");

      if (!normalizedAreaId || this.isAreaOperationRunning) {
        return;
      }

      this.deletingAreaId = normalizedAreaId;

      this.$emit("delete-area", {
        areaId: normalizedAreaId,

        done: async () => {
          this.deletingAreaId = null;

          await Swal.fire({
  icon: "success",
  title: "Liga eliminada",
  text: "La liga se ha eliminado correctamente.",

  confirmButtonText: "Entendido",

  buttonsStyling: false,

  customClass: {
    popup: "liga-eliminada-popup",
    confirmButton: "liga-eliminada-confirmar",
  },
});
        },

        fail: async (message) => {
          this.deletingAreaId = null;

          await Swal.fire({
            icon: "error",
            title: "No se pudo eliminar",
            text: message || "No se pudo eliminar la liga.",
            confirmButtonText: "Entendido",
        customClass: {
          confirmButton: "clausulazo-swal-confirm",
        },
          });
        },
      });
    },

    async confirmLeaveArea(area) {
      if (!area || this.isAreaOperationRunning) {
        return;
      }

      const safeAreaName = this.escapeHtml(area.name);

      const result = await Swal.fire({
        icon: "question",
        title: "¿Salir de la liga?",
        html: `
          <p>Vas a salir de <strong>${safeAreaName}</strong>.</p>
          <p>
            Seguirás perteneciendo a la liga y podrás volver a entrar
            cuando quieras.
          </p>
        `,
        showCancelButton: true,
        confirmButtonText: "SÍ, SALIR",
        cancelButtonText: "CANCELAR",
        reverseButtons: false,
        buttonsStyling: false,

        customClass: {
          popup: "salir-liga-popup",
          confirmButton: "salir-liga-confirmar",
          cancelButton: "salir-liga-cancelar",
          actions: "salir-liga-acciones",
        },
      });

      if (result.isConfirmed) {
        this.leaveArea(area.id);
      }
    },

    leaveArea(areaId) {
      const normalizedAreaId = String(areaId || "");

      if (!normalizedAreaId || this.isAreaOperationRunning) {
        return;
      }

      this.leavingAreaId = normalizedAreaId;

      this.$emit("leave-area", {
        areaId: normalizedAreaId,

        done: () => {
          this.leavingAreaId = null;
        },

        fail: async (message) => {
          this.leavingAreaId = null;

          await Swal.fire({
            icon: "error",
            title: "No se pudo salir",
            text: message || "No se pudo salir de la liga.",
            confirmButtonText: "Entendido",
        customClass: {
          confirmButton: "clausulazo-swal-confirm",
        },
          });
        },
      });
    },

    enterArea(areaId) {
      const normalizedAreaId = String(areaId || "");

      if (!normalizedAreaId || this.isAreaOperationRunning) {
        return;
      }

      this.enteringAreaId = normalizedAreaId;

      this.$emit("enter-area", {
        areaId: normalizedAreaId,

        done: () => {
          this.enteringAreaId = null;
        },

        fail: () => {
          this.enteringAreaId = null;
        },
      });
    },

   createLeague() {
      this.createError = "";

      const name = String(
        this.newLeagueName || ""
      ).trim();

      const durationDays = Number(
        this.newLeagueDurationDays
      );

      if (!name) {
        this.createError =
          "Escribe un nombre para la liga";
        return;
      }

      if (
        !Number.isInteger(durationDays) ||
        durationDays < 1
      ) {
        this.createError =
          "La duración debe ser de al menos 1 día";
        return;
      }

      if (this.loadingCreate) return;

      this.loadingCreate = true;

      this.$emit("create-area", {
        name,
        durationDays,

        done: () => {
          this.loadingCreate = false;
          this.newLeagueName = "";
          this.newLeagueDurationDays = 60;
          this.showCreateDialog = false;
          this.createError = "";
        },

        fail: (message) => {
          this.loadingCreate = false;

          this.createError =
            message ||
            "No se pudo crear la liga";
        },
      });
    },

    joinLeagueByCode() {
      this.joinError = "";

      const code = String(this.joinCode || "")
        .trim()
        .toUpperCase();

      if (!code) {
        this.joinError = "Introduce el código de la liga";
        return;
      }

      if (this.loadingJoin) return;

      this.loadingJoin = true;

      this.$emit("join-area-code", {
        code,

        done: () => {
          this.loadingJoin = false;
          this.joinCode = "";
          this.showJoinDialog = false;
          this.joinError = "";
        },

        fail: (message) => {
          this.loadingJoin = false;
          this.joinError = message || "No se pudo unir a la liga";
        },
      });
    },

    normalizeJoinCode(value) {
      this.joinCode = String(value || "")
        .trimStart()
        .toUpperCase();
    },

    closeCreateDialog() {
      if (this.loadingCreate) return;

      this.showCreateDialog = false;
      this.newLeagueName = "";
      this.newLeagueDurationDays = 60;
      this.createError = "";
    },

    closeJoinDialog() {
      if (this.loadingJoin) return;

      this.showJoinDialog = false;
      this.joinCode = "";
      this.joinError = "";
    },

    async copyCode(code) {
      try {
        await navigator.clipboard.writeText(String(code || ""));
      } catch (error) {
        console.warn("No se pudo copiar el código", error);
      }
    },
  },
};
</script>

<style scoped>
.players-close-btn {
  width: 40px !important;
  height: 40px !important;
  min-width: 40px !important;
  background-color: #f1f1f1 !important;
}

.players-close-icon {
  font-size: 24px !important;
  color: #555555 !important;
  line-height: 1 !important;
}

.players-close-btn:hover {
  background-color: #e4e4e4 !important;
}
.orbitron-title {
  font-family: "Orbitron", sans-serif !important;
  font-size: 20px;
  font-weight: 600;
}

.area-card {
  overflow: visible;
}

.area-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
}

.area-heading {
  flex: 1 1 auto;
  min-width: 0;
}

.delete-icon-btn {
  flex: 0 0 auto;
  width: 42px !important;
  height: 42px !important;
  min-width: 42px !important;
  border: 2px solid #b71c1c !important;
  border-radius: 10px !important;
  background-color: #fff5f5 !important;
  color: #b71c1c !important;
  box-shadow: none !important;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease,
    transform 0.2s ease,
    box-shadow 0.2s ease !important;
}

.delete-icon-btn:hover {
  background-color: #ffebee !important;
  border-color: #8e1010 !important;
  color: #8e1010 !important;
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(183, 28, 28, 0.16) !important;
}

.delete-icon {
  font-size: 22px !important;
  color: inherit !important;
}

.area-actions {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
}



.crear-liga-btn,
.unirse-liga-btn,
.entrar-liga-btn,
.salir-area {
  
  border-radius: 6px !important;
  font-family: "Orbitron", sans-serif !important;
  font-size: 15px !important;
  font-weight: 500 !important;
  line-height: 1 !important;
  letter-spacing: 1px !important;
  text-transform: uppercase !important;
  box-shadow: none !important;
}

.crear-liga-btn,
.unirse-liga-btn,
.entrar-liga-btn {
  padding: 0 20px !important;
  border: 2px solid #31635f !important;
  color: #31635f !important;
  transition:
    background-color 0.25s ease,
    border-color 0.25s ease,
    color 0.25s ease,
    transform 0.25s ease,
    box-shadow 0.25s ease !important;
}

.crear-liga-btn,
.entrar-liga-btn {
  background-color: #edf7f6 !important;
}

.unirse-liga-btn {
  background-color: #ffffff !important;
}

.crear-liga-btn:hover,
.unirse-liga-btn:hover,
.entrar-liga-btn:hover {
  background-color: #dcefeb !important;
  border-color: #244c49 !important;
  color: #244c49 !important;
  transform: translateY(-1px);
  box-shadow: 0 5px 13px rgba(49, 99, 95, 0.18) !important;
}

.area-btn-icon {
  margin-right: 8px;
  font-size: 20px !important;
  color: inherit !important;
}

.salir-area {
  padding: 0 18px !important;
  border: 2px solid #d32f2f !important;
  background-color: #ffffff !important;
  color: #d32f2f !important;
  transition:
    background-color 0.25s ease,
    transform 0.25s ease !important;
}

.salir-area:hover {
  background-color: #ffebee !important;
  transform: translateY(-1px);
}

.crear-liga-btn.v-btn--disabled,
.unirse-liga-btn.v-btn--disabled,
.entrar-liga-btn.v-btn--disabled,
.salir-area.v-btn--disabled,
.delete-icon-btn.v-btn--disabled {
  background-color: #f1f3f3 !important;
  border-color: #b8c3c2 !important;
  color: #8b9695 !important;
  box-shadow: none !important;
  transform: none !important;
}

.alert-ligas-vacio {
  background-color: #ffffff !important;
  border: 2px solid #31635f !important;
  color: #31635f !important;
}
.league-countdown {
  font-family: "Orbitron", sans-serif !important;
  white-space: nowrap;
}

.league-countdown-finished {
  color: #757575 !important;
}

@media (max-width: 600px) {
  .orbitron-title {
    font-size: 17px;
  }

  .area-actions,
  .area-actions-active {
    grid-template-columns: 1fr;
  }

  .crear-liga-btn,
  .unirse-liga-btn,
  .entrar-liga-btn,
  .salir-area {
   
    padding: 0 12px !important;
    font-size: 11px !important;
    letter-spacing: 0.6px !important;
  }

  .area-btn-icon {
    margin-right: 6px;
    font-size: 18px !important;
  }

  .delete-icon-btn {
    width: 38px !important;
    height: 38px !important;
    min-width: 38px !important;
  }

  .delete-icon {
    font-size: 20px !important;
  }
}
</style>

<style>
/* SweetAlert se monta en body; por eso estos estilos no pueden ser scoped. */
.swal2-popup.salir-liga-popup {
  width: min(520px, calc(100vw - 32px)) !important;
  padding: 28px 28px 24px !important;
  border-radius: 18px !important;
}

.salir-liga-popup .swal2-title {
  padding: 0 !important;
  margin-top: 8px !important;
  color: #444444 !important;
  font-family: "Orbitron", sans-serif !important;
  font-size: 27px !important;
  font-weight: 700 !important;
  line-height: 1.25 !important;
}

.salir-liga-popup .swal2-html-container {
  margin: 22px 0 8px !important;
  color: #5c5c5c !important;
  font-size: 17px !important;
  line-height: 1.45 !important;
  text-align: center !important;
}

.salir-liga-popup .swal2-html-container p {
  margin: 0 0 7px !important;
}

.swal2-actions.salir-liga-acciones {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  flex-wrap: nowrap !important;
  width: 100% !important;
  margin: 24px 0 0 !important;
  gap: 14px !important;
}

.swal2-confirm.salir-liga-confirmar,
.swal2-cancel.salir-liga-cancelar {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  min-width: 150px !important;
  height: 46px !important;
  margin: 0 !important;
  padding: 0 22px !important;
  border: none !important;
  border-radius: 999px !important;
  color: #ffffff !important;
  font-family: "Orbitron", sans-serif !important;
  font-size: 12px !important;
  font-weight: 700 !important;
  line-height: 1 !important;
  letter-spacing: 0.4px !important;
  text-transform: uppercase !important;
  cursor: pointer !important;
}

.swal2-confirm.salir-liga-confirmar {
  background: #d9363e !important;
  box-shadow: 0 7px 18px rgba(217, 54, 62, 0.28) !important;
}

.swal2-confirm.salir-liga-confirmar:hover {
  background: #bd2931 !important;
  transform: translateY(-1px);
}

.swal2-cancel.salir-liga-cancelar {
  background: #4c5869 !important;
  box-shadow: 0 7px 18px rgba(50, 60, 75, 0.25) !important;
}

.swal2-cancel.salir-liga-cancelar:hover {
  background: #3c4655 !important;
  transform: translateY(-1px);
}

@media (max-width: 500px) {
  .swal2-popup.salir-liga-popup {
    width: calc(100vw - 22px) !important;
    padding: 22px 14px 18px !important;
  }

  .salir-liga-popup .swal2-title {
    font-size: 21px !important;
  }

  .salir-liga-popup .swal2-html-container {
    font-size: 15px !important;
  }

  .swal2-actions.salir-liga-acciones {
    gap: 8px !important;
  }

  .swal2-confirm.salir-liga-confirmar,
  .swal2-cancel.salir-liga-cancelar {
    flex: 1 1 0 !important;
    min-width: 0 !important;
    height: 43px !important;
    padding: 0 10px !important;
    font-size: 10px !important;
  }
}
.swal2-popup.eliminar-liga-popup {
  width: min(620px, calc(100vw - 32px)) !important;
  padding: 28px 30px 24px !important;
  border-radius: 18px !important;
}

.eliminar-liga-popup .swal2-title {
  color: #4d4d4d !important;
  font-family: "Orbitron", sans-serif !important;
  font-size: 28px !important;
  font-weight: 700 !important;
}

.eliminar-liga-popup .swal2-html-container {
  color: #555555 !important;
  font-size: 17px !important;
  line-height: 1.5 !important;
}

.swal2-input.eliminar-liga-input {
  width: calc(100% - 20px) !important;
  height: 52px !important;
  margin: 20px auto 4px !important;

  border: 2px solid #9fc6df !important;
  border-radius: 8px !important;

  color: #4d4d4d !important;
  font-size: 18px !important;

  box-shadow: none !important;
}

.swal2-input.eliminar-liga-input:focus {
  border-color: #58a39e !important;

  box-shadow:
    0 0 0 3px rgba(88, 163, 158, 0.18) !important;
}

.swal2-actions.eliminar-liga-acciones {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;

  width: 100% !important;
  margin: 26px 0 0 !important;
  gap: 14px !important;
}

/* Base común de los dos botones */
.swal2-confirm.eliminar-liga-confirmar,
.swal2-cancel.eliminar-liga-cancelar {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;

  min-width: 160px !important;
  height: 48px !important;
  margin: 0 !important;
  padding: 0 22px !important;

  border: none !important;
  border-radius: 999px !important;

  color: #ffffff !important;

  font-family: "Orbitron", sans-serif !important;
  font-size: 12px !important;
  font-weight: 700 !important;
  line-height: 1 !important;
  letter-spacing: 0.4px !important;
  text-transform: uppercase !important;

  cursor: pointer !important;

  transition:
    background-color 0.2s ease,
    transform 0.2s ease,
    box-shadow 0.2s ease !important;
}

/* Eliminar */
.swal2-confirm.eliminar-liga-confirmar {
  background-color: #d32f2f !important;
  color: #ffffff !important;

  box-shadow:
    0 7px 18px rgba(211, 47, 47, 0.28) !important;
}

.swal2-confirm.eliminar-liga-confirmar:hover {
  background-color: #b71c1c !important;
  color: #ffffff !important;
  transform: translateY(-1px);

  box-shadow:
    0 9px 22px rgba(211, 47, 47, 0.36) !important;
}

/* Cancelar */
.swal2-cancel.eliminar-liga-cancelar {
  background-color: #4c5869 !important;
  color: #ffffff !important;

  box-shadow:
    0 7px 18px rgba(50, 60, 75, 0.25) !important;
}

.swal2-cancel.eliminar-liga-cancelar:hover {
  background-color: #3c4655 !important;
  color: #ffffff !important;
  transform: translateY(-1px);
}

/* =========================================================
   RESULTADO: LIGA ELIMINADA
   ========================================================= */

.swal2-popup.liga-eliminada-popup {
  width: min(520px, calc(100vw - 32px)) !important;
  padding: 30px 28px 26px !important;
  border-radius: 18px !important;
}

.liga-eliminada-popup .swal2-title {
  color: #4d4d4d !important;
  font-family: "Orbitron", sans-serif !important;
  font-size: 28px !important;
  font-weight: 700 !important;
}

.liga-eliminada-popup .swal2-html-container {
  color: #555555 !important;
  font-size: 17px !important;
  line-height: 1.5 !important;
}

/* Entendido */
.swal2-confirm.liga-eliminada-confirmar {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;

  min-width: 160px !important;
  height: 48px !important;
  margin: 8px 0 0 !important;
  padding: 0 24px !important;

  border: none !important;
  border-radius: 999px !important;

  background-color: #31635f !important;
  color: #ffffff !important;

  font-family: "Orbitron", sans-serif !important;
  font-size: 12px !important;
  font-weight: 700 !important;
  line-height: 1 !important;
  letter-spacing: 0.4px !important;
  text-transform: uppercase !important;

  cursor: pointer !important;

  box-shadow:
    0 7px 18px rgba(49, 99, 95, 0.28) !important;

  transition:
    background-color 0.2s ease,
    transform 0.2s ease,
    box-shadow 0.2s ease !important;
}

.swal2-confirm.liga-eliminada-confirmar:hover {
  background-color: #244c49 !important;
  color: #ffffff !important;
  transform: translateY(-1px);

  box-shadow:
    0 9px 22px rgba(49, 99, 95, 0.36) !important;
}

.swal2-confirm.eliminar-liga-confirmar:focus,
.swal2-cancel.eliminar-liga-cancelar:focus,
.swal2-confirm.liga-eliminada-confirmar:focus {
  color: #ffffff !important;
  outline: none !important;
}
.admin-league-actions {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  width: 100%;
}

.admin-league-action-btn {
  width: 100% !important;
  height: 36px !important;
  min-width: 0 !important;
}

.manage-players-btn {
  border-radius: 6px !important;
  padding: 0 20px !important;
  border: 2px solid #31635f !important;
  background-color: #ffffff !important;
  color: #31635f !important;

  font-family: "Orbitron", sans-serif !important;
  font-size: 15px !important;
  font-weight: 500 !important;
  line-height: 1 !important;
  letter-spacing: 1px !important;
  text-transform: uppercase !important;

  box-shadow: none !important;

  transition:
    background-color 0.25s ease,
    border-color 0.25s ease,
    color 0.25s ease,
    transform 0.25s ease,
    box-shadow 0.25s ease !important;
}

.manage-players-btn:hover {
  background-color: #dcefeb !important;
  border-color: #244c49 !important;
  color: #244c49 !important;
  transform: translateY(-1px);
  box-shadow: 0 5px 13px rgba(49, 99, 95, 0.18) !important;
}
.players-manager-card {
  border-radius: 18px !important;
}

.players-manager-title {
  display: flex;
  align-items: center;
  padding: 20px 24px !important;
}

.player-manager-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 4px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.player-manager-row:last-child {
  border-bottom: none;
}

.player-manager-info {
  display: flex;
  align-items: center;
  min-width: 0;
}

.expel-player-btn {
  flex-shrink: 0;
  font-family: "Orbitron", sans-serif !important;
  text-transform: none !important;
  color: #c62828 !important;
  border-color: #c62828 !important;
}


/* Móvil */
@media (max-width: 500px) {
  .swal2-popup.eliminar-liga-popup,
  .swal2-popup.liga-eliminada-popup {
    width: calc(100vw - 22px) !important;
    padding: 22px 15px 18px !important;
  }

  .eliminar-liga-popup .swal2-title,
  .liga-eliminada-popup .swal2-title {
    font-size: 21px !important;
  }

  .eliminar-liga-popup .swal2-html-container,
  .liga-eliminada-popup .swal2-html-container {
    font-size: 15px !important;
  }

  .swal2-actions.eliminar-liga-acciones {
    gap: 8px !important;
  }

  .swal2-confirm.eliminar-liga-confirmar,
  .swal2-cancel.eliminar-liga-cancelar {
    flex: 1 1 0 !important;
    min-width: 0 !important;
    height: 44px !important;
    padding: 0 10px !important;
    font-size: 10px !important;
  }

  .swal2-confirm.liga-eliminada-confirmar {
    min-width: 145px !important;
    height: 44px !important;
    font-size: 10px !important;
  }
  .admin-league-actions {
  grid-template-columns: 1fr;
}

.admin-league-action-btn {
  width: 100% !important;
}

.manage-players-btn {
  padding: 0 12px !important;
  font-size: 11px !important;
  letter-spacing: 0.6px !important;
}
}
.swal2-popup.iniciar-liga-popup {
  width: min(520px, calc(100vw - 32px)) !important;
  padding: 28px 28px 24px !important;
  border-radius: 18px !important;
}

.iniciar-liga-popup .swal2-title {
  color: #4d4d4d !important;
  font-family: "Orbitron", sans-serif !important;
  font-size: 27px !important;
  font-weight: 700 !important;
}

.iniciar-liga-content {
  text-align: left;
  color: #555555;
  font-size: 16px;
}

.iniciar-liga-content p {
  margin: 0 0 8px;
}

.iniciar-liga-label {
  display: block;
  margin-top: 20px;
  margin-bottom: 7px;
  font-family: "Orbitron", sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #31635f;
}

.iniciar-liga-duration-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
}

.iniciar-liga-duration-input {
  width: 130px;
  height: 45px;
  padding: 0 12px;
  border: 2px solid #9fc6df;
  border-radius: 8px;
  outline: none;
  font-size: 17px;
}

.iniciar-liga-duration-input:focus {
  border-color: #31635f;
  box-shadow: 0 0 0 3px rgba(49, 99, 95, 0.14);
}

.iniciar-liga-duration-suffix {
  font-size: 15px;
  color: #555555;
}

.iniciar-liga-hint {
  margin-top: 6px;
  color: #888888;
  font-size: 12px;
}

.iniciar-liga-warning {
  margin-top: 20px !important;
}

.swal2-actions.iniciar-liga-acciones {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  flex-wrap: nowrap !important;
  width: 100% !important;
  margin: 24px 0 0 !important;
  gap: 14px !important;
}

.swal2-confirm.iniciar-liga-confirmar,
.swal2-cancel.iniciar-liga-cancelar {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;

  min-width: 150px !important;
  height: 46px !important;

  margin: 0 !important;
  padding: 0 22px !important;

  border: none !important;
  border-radius: 999px !important;

  color: #ffffff !important;

  font-family: "Orbitron", sans-serif !important;
  font-size: 11px !important;
  font-weight: 700 !important;

  text-transform: uppercase !important;
  cursor: pointer !important;
}

.swal2-confirm.iniciar-liga-confirmar {
  background: #31635f !important;
  box-shadow: 0 7px 18px rgba(49, 99, 95, 0.28) !important;
}

.swal2-confirm.iniciar-liga-confirmar:hover {
  background: #244c49 !important;
}

.swal2-cancel.iniciar-liga-cancelar {
  background: #4c5869 !important;
  box-shadow: 0 7px 18px rgba(50, 60, 75, 0.25) !important;
}

.swal2-cancel.iniciar-liga-cancelar:hover {
  background: #3c4655 !important;
}

@media (max-width: 500px) {
  .swal2-actions.iniciar-liga-acciones {
    gap: 8px !important;
  }

  .swal2-confirm.iniciar-liga-confirmar,
  .swal2-cancel.iniciar-liga-cancelar {
    flex: 1 1 0 !important;
    min-width: 0 !important;
    padding: 0 10px !important;
    font-size: 9px !important;
  }
}
.swal2-popup.expulsar-jugador-popup {
  width: min(560px, calc(100vw - 32px)) !important;
  padding: 28px 30px 24px !important;
  border-radius: 18px !important;
}

.expulsar-jugador-popup .swal2-title {
  color: #4d4d4d !important;
  font-family: "Orbitron", sans-serif !important;
  font-size: 28px !important;
  font-weight: 700 !important;
}

.expulsar-jugador-popup .swal2-html-container {
  color: #555555 !important;
  font-size: 17px !important;
  line-height: 1.4 !important;
}

.swal2-actions.expulsar-jugador-acciones {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  flex-wrap: nowrap !important;

  width: 100% !important;
  margin: 26px 0 0 !important;
  gap: 14px !important;
}

.swal2-confirm.expulsar-jugador-confirmar,
.swal2-cancel.expulsar-jugador-cancelar {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;

  min-width: 170px !important;
  height: 48px !important;

  margin: 0 !important;
  padding: 0 22px !important;

  border: none !important;
  border-radius: 999px !important;

  color: #ffffff !important;

  font-family: "Orbitron", sans-serif !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  line-height: 1 !important;
  letter-spacing: 0.4px !important;
  text-transform: uppercase !important;

  cursor: pointer !important;

  transition:
    background-color 0.2s ease,
    transform 0.2s ease,
    box-shadow 0.2s ease !important;
}

.swal2-confirm.expulsar-jugador-confirmar {
  background-color: #d32f2f !important;
  box-shadow: 0 7px 18px rgba(211, 47, 47, 0.28) !important;
}

.swal2-confirm.expulsar-jugador-confirmar:hover {
  background-color: #b71c1c !important;
  transform: translateY(-1px);
  box-shadow: 0 9px 22px rgba(211, 47, 47, 0.36) !important;
}

.swal2-cancel.expulsar-jugador-cancelar {
  background-color: #4c5869 !important;
  box-shadow: 0 7px 18px rgba(50, 60, 75, 0.25) !important;
}

.swal2-cancel.expulsar-jugador-cancelar:hover {
  background-color: #3c4655 !important;
  transform: translateY(-1px);
}

.swal2-confirm.expulsar-jugador-confirmar:focus,
.swal2-cancel.expulsar-jugador-cancelar:focus {
  color: #ffffff !important;
  outline: none !important;
}

@media (max-width: 500px) {
  .swal2-popup.expulsar-jugador-popup {
    width: calc(100vw - 22px) !important;
    padding: 22px 15px 18px !important;
  }

  .expulsar-jugador-popup .swal2-title {
    font-size: 21px !important;
  }

  .expulsar-jugador-popup .swal2-html-container {
    font-size: 15px !important;
  }

  .swal2-actions.expulsar-jugador-acciones {
    gap: 8px !important;
  }

  .swal2-confirm.expulsar-jugador-confirmar,
  .swal2-cancel.expulsar-jugador-cancelar {
    flex: 1 1 0 !important;
    min-width: 0 !important;
    height: 44px !important;
    padding: 0 10px !important;
    font-size: 9px !important;
  }
}
</style>
